import { useSelector } from 'react-redux';

import { isCoursesMockEnabled } from '@/services/coursesMockFlag';
import type { RootState } from '@/store/store';
import type { ResolvedInstance } from '@/types/Instance';
import type { User } from '@/types/User';
import { resolveCapabilities, type InstanceCapabilities } from './capabilities';
import {
  isPlainOraViewOver,
  usePlainOraViewFlag,
} from '../mcgrawHill/plainOraView'; // mcgraw-hill

export const DEFAULT_INSTANCE: ResolvedInstance = {
  id: null,
  slug: 'default',
  displayName: 'Ora',
  brand: {
    logoUrl: null,
    wordmarkUrl: null,
    accentColor: null,
  },
  capabilities: {},
  source: 'default',
};

export const resolveProfileInstance = (
  profile: Pick<User, 'instance'> | null | undefined
): ResolvedInstance => {
  const instance = profile?.instance ?? DEFAULT_INSTANCE;
  // mcgraw-hill (ORA-1268): a member's plain Ora view is the default instance.
  return isPlainOraViewOver(instance.slug) ? DEFAULT_INSTANCE : instance;
};

export const useInstance = (): ResolvedInstance => {
  // Read defensively: a narrow test store (the chat's, the desktop's) carries
  // no user slice, and the answer there is the default instance.
  const profile = useSelector(
    (state: Partial<RootState>) => state.user?.profile
  );

  usePlainOraViewFlag(); // mcgraw-hill: re-resolve when the plain view flips

  // Feature code reads capabilities; see instanceSlugs.ts for the narrow
  // exception for partner-specific experiences.
  return resolveProfileInstance(profile);
};

/** Select only the instance slug so unrelated profile updates do not rerender
 * consumers that only need to identify the current instance. */
export const useInstanceSlug = (): string => {
  // mcgraw-hill: re-render when the plain view flips; the inline selector
  // below then runs again.
  usePlainOraViewFlag();

  return useSelector(
    (state: Partial<RootState>) =>
      resolveProfileInstance(state.user?.profile).slug
  );
};

/** Whether the current user belongs to one specific instance. */
export const useIsInstance = (slug: string): boolean =>
  useInstanceSlug() === slug;

/** The loader-safe counterpart of `useIsInstance`. */
export const isProfileInInstance = (
  profile: Pick<User, 'instance'> | null | undefined,
  slug: string
): boolean => resolveProfileInstance(profile).slug === slug;

/**
 * One typed capability of the student's instance, with its default filled in
 * when the manifest leaves it out or sends a value the frontend does not know.
 * Each gate reads the capability it is about (`studyLibrary` for the study
 * tabs, `commerce` for billing), not the course home switch.
 */
export const useCapability = <K extends keyof InstanceCapabilities>(
  key: K
): InstanceCapabilities[K] => {
  const { capabilities } = useInstance();

  return resolveCapabilities(capabilities)[key];
};

/**
 * Whether the student is in the partner-course experience (Courses V1): the
 * `homeVariant` switch the course home, the header's course selector, the
 * nav's course items and the course-only Topics and Performance views hang
 * off. Library, commerce, theme and brand gates read their own capability
 * through `useCapability`. True when the
 * student's instance (the partner organization they hold a seat in, or an
 * admin override) has `homeVariant: 'course'` (ORA-1054), or in a dev build
 * serving the course flow from its fixture (`isCoursesMockEnabled` is false
 * in every production build). A COURSE primary exam alone does not switch
 * it on: the partner's instance owns the experience.
 */
export const useIsCourseStudent = (): boolean => {
  const profile = useSelector(
    (state: Partial<RootState>) => state.user?.profile
  );

  usePlainOraViewFlag(); // mcgraw-hill: re-resolve when the plain view flips

  return isCourseStudentProfile(profile);
};

/**
 * `useIsCourseStudent` for code that holds the profile rather than rendering
 * (a route loader reading the store): the same rule, no hook.
 */
export const isCourseStudentProfile = (
  profile: Pick<User, 'instance'> | null | undefined
): boolean =>
  resolveCapabilities(resolveProfileInstance(profile).capabilities)
    .homeVariant === 'course' || isCoursesMockEnabled();
